// components
import Tooltip from '@mui/material/Tooltip';
import { DateNavigation } from 'components/shared';
import CreateButton from 'components/shared/CreateButton/CreateButton';
import EmployeePickerDialog from './EmployeePickerDialog';
import IconButton from '@mui/material/IconButton';
import ReactRegisterAbsenceDialog from 'components/shared/RegisterAbsence/RegisterAbsenceDialog';
import WorkingDaysSettingsDialog from 'components/shared/RegisterAbsence/WorkingDaysSettingsDialog';
import { Icon } from '@sdb/planning-ui';
import TableCRUDLayout from '../CRUD/TableCRUDLayout';

// hooks
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useSelector } from 'hooks';

// styles
import {
  DateNavigationActions,
  DateNavigationCenter,
  DateNavigationContainer,
  EllipsisBox
} from './AbsenceNotices.styles';

// utils
import { isBefore } from 'date-fns';
import {
  flattenToDate,
  formatDate,
  formatDateTime,
  getMonthInterval,
  getYearInterval,
  parseBackendDateTime,
  toDateString
} from 'utils/date';
import { mutations, queries, queryKeys } from 'RestAPIs/rapini';
import { openRegisterAbsenceDialog } from './registerAbsenceDialog';

// types
import type { Employee } from 'components/shared/EmployeeSelector';
import type { GridRenderCellParams } from '@mui/x-data-grid-pro';
import type { IconType } from '@sdb/planning-ui/ui-3/packages/icons/__generated__';
import type {
  RegisterAbsenceDialogEmployee,
  RegisterAbsenceDialogNotice
} from 'components/shared/RegisterAbsence/RegisterAbsenceDialog.types';

// Absences registered from the notice flows are sickness reports, so the
// register dialog only offers the sickness duty hour types:
// ziekte (ZK) and ziekte t.g.v. zwangerschap (ZKZ)
const SICKNESS_DUTY_HOUR_TYPE_CODES = ['ZK', 'ZKZ'];

/**
 * Deep link to the employee card, opened on its absence-notices tab. `page=`
 * stays in the hash so this screen does not flash back to its default tab
 * while the views are being swapped.
 */
const employeeCardHref = (employeeDbId: number | string) =>
  `#roster,employees&page=absence-notices&empId=${employeeDbId}&tab=absenceNotices`;

type AbsenceNoticesProps = {
  /**
   * When set, the table shows the notice history of this single employee
   * (employee-card context): a year period instead of a month.
   */
  employeeId?: number;
  /** Name of the employee, used to register a new absence from the employee card */
  employeeName?: string;
  /** Employee number, used to register a new absence from the employee card */
  employeeNumber?: number | string;
};

const AbsenceNotices = ({
  employeeId,
  employeeName,
  employeeNumber
}: AbsenceNoticesProps) => {
  'use memo';

  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const {
    hasRegisterAbsenceAccess,
    hasAbsenceNoticesViewAccess,
    hasAbsenceNoticesProcessAccess,
    hasFeatureToggleReactRegisterAbsence
  } = useSelector((state) => state.grants);
  const isEmployeeHistory = employeeId !== undefined;
  const getInterval = isEmployeeHistory ? getYearInterval : getMonthInterval;
  const { startDate } = getInterval(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(startDate);
  const [employeePickerOpen, setEmployeePickerOpen] = useState(false);
  const [settingsEmployee, setSettingsEmployee] =
    useState<RegisterAbsenceDialogEmployee | null>(null);
  const [reactDialog, setReactDialog] = useState<{
    employee: RegisterAbsenceDialogEmployee;
    notice?: RegisterAbsenceDialogNotice;
    absenceId?: number;
    onSuccess?: () => void;
  } | null>(null);
  const interval = getInterval(selectedDate);
  const { data: absenceNotices, isLoading } = queries.useGetAbsenceNotices(
    toDateString(interval.startDate),
    toDateString(interval.endDate),
    employeeId
  );
  const { mutate: processAbsenceNotice } = mutations.useProcessAbsenceNotice();

  const refreshAbsenceNotices = () =>
    queryClient.invalidateQueries(queryKeys.getAbsenceNotices.name);

  const onClickProcess = (id: string) => {
    const notice = absenceNotices?.find((d) => d.entityId === id);

    // With register-absence access the notice is processed by actually
    // registering the absence; without it the notice is only marked processed.
    if (hasRegisterAbsenceAccess && notice?.employee?.id) {
      const employeeArg = {
        id: notice.employee.id,
        name: notice.employee.nameInformal,
        number: notice.employee.number
      };
      const markProcessed = () => {
        processAbsenceNotice(
          { absenceNoticeId: id },
          { onSettled: refreshAbsenceNotices }
        );
      };

      if (hasFeatureToggleReactRegisterAbsence) {
        setReactDialog({
          employee: employeeArg,
          notice: {
            start: notice.start,
            end: notice.end,
            reason: notice.reason,
            createdBy: notice.createdBy
          },
          onSuccess: markProcessed
        });
        return;
      }

      openRegisterAbsenceDialog({
        employee: employeeArg,
        startDate: new Date(notice.start),
        endDate: new Date(notice.end),
        allowedDutyHourTypeCodes: SICKNESS_DUTY_HOUR_TYPE_CODES,
        onSuccess: markProcessed
      });
      return;
    }

    processAbsenceNotice(
      { absenceNoticeId: id },
      { onSettled: refreshAbsenceNotices }
    );
  };

  const registerAbsenceForEmployee = (employee: {
    id: number;
    name?: string;
    number?: number | string;
  }) => {
    if (hasFeatureToggleReactRegisterAbsence) {
      setReactDialog({ employee, onSuccess: refreshAbsenceNotices });
      return;
    }

    openRegisterAbsenceDialog({
      employee,
      allowedDutyHourTypeCodes: SICKNESS_DUTY_HOUR_TYPE_CODES,
      onSuccess: refreshAbsenceNotices
    });
  };

  const onClickNew = () => {
    if (employeeId !== undefined) {
      registerAbsenceForEmployee({
        id: employeeId,
        name: employeeName,
        number: employeeNumber
      });
      return;
    }

    setEmployeePickerOpen(true);
  };

  const onEmployeePicked = (employee: Employee) => {
    setEmployeePickerOpen(false);

    if (!employee?.id) {
      return;
    }

    registerAbsenceForEmployee({
      id: employee.id,
      name: employee.employeeName ?? employee.label,
      number: employee.nr
    });
  };

  /** only reachable from the employee card, where the employee is known */
  const onClickSettings = () => {
    if (employeeId !== undefined) {
      setSettingsEmployee({
        id: employeeId,
        name: employeeName,
        number: employeeNumber
      });
    }
  };

  const columns = [
    {
      field: 'employeeNumber',
      headerName: t('absenceNoticeEmployeeNumberShort'),
      description: t('employeeNr'),
      width: 80
    },
    {
      field: 'workAgreement',
      headerName: t('absenceNoticeWorkAgreementShort'),
      description: t('employment'),
      width: 56,
      // the source plan unit is shown as a tooltip on the work agreement
      // number, which keeps the table narrow
      renderCell: (params: GridRenderCellParams) =>
        params.value && params.row.planUnit ? (
          <Tooltip
            title={`${t('absenceNoticeSourcePlanUnit')}: ${
              params.row.planUnit
            }`}
            arrow
          >
            <EllipsisBox>{params.value}</EllipsisBox>
          </Tooltip>
        ) : (
          params.value
        )
    },
    { field: 'employee', headerName: t('employee'), flex: 1.2, minWidth: 150 },
    { field: 'start', headerName: t('startTime'), width: 112 },
    { field: 'end', headerName: t('endTime'), width: 112 },
    { field: 'status', headerName: t('status'), width: 100 },
    {
      field: 'createdOn',
      headerName: t('createdOn'),
      width: 100,
      // date only in the cell; the exact time lives in the tooltip
      renderCell: (params: GridRenderCellParams) =>
        params.value ? (
          <Tooltip title={params.row.createdOnFull} arrow>
            <EllipsisBox>{params.value}</EllipsisBox>
          </Tooltip>
        ) : null
    },
    { field: 'createdBy', headerName: t('createdBy'), flex: 1, minWidth: 140 },
    {
      field: 'reason',
      headerName: t('reason'),
      flex: 1.4,
      minWidth: 150,
      // long reasons stay readable: the cell truncates with an ellipsis and
      // shows the full text in a tooltip
      renderCell: (params: GridRenderCellParams) =>
        params.value ? (
          <Tooltip title={params.value} arrow>
            <EllipsisBox>{params.value}</EllipsisBox>
          </Tooltip>
        ) : null
    }
  ];

  const rows =
    absenceNotices
      ?.filter(
        (d) => !isBefore(parseBackendDateTime(d.start), interval.startDate)
      )
      // newest notices on top: sorted by creation moment, so what just came
      // in or was just registered is the first thing the planner sees
      ?.sort(
        (a, b) =>
          new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime()
      )
      .map((d) => ({
        id: d.entityId,
        // only registered rows carry the real absence id; its presence is
        // what makes the edit action appear
        absenceId: d.absenceId ? Number(d.absenceId) : undefined,
        employeeNumber: d.employee?.number,
        employeeDbId: d.employee?.id,
        employeeName: d.employee?.nameInformal,
        workAgreement: d.workAgreement,
        planUnit: d.planUnit,
        employee: d.employee?.nameInformal,
        status: t(`absenceNoticeStatus.${d.status}`),
        start: formatDateTime(d.start),
        end: formatDateTime(d.end),
        createdOn: formatDate(d.createdOn),
        createdOnFull: formatDateTime(d.createdOn),
        createdBy: d.createdBy,
        reason: d.reason
      })) ?? [];

  // The screen guards itself rather than trusting whoever mounted it. On the
  // employee card the Dojo bridge renders this component directly, so the tab
  // condition there was the only gate — one missed frontend build and the whole
  // screen is served to a tenant that should not have it.
  if (!hasAbsenceNoticesViewAccess) {
    return null;
  }

  return (
    <>
      <DateNavigationContainer>
        <DateNavigationCenter>
          <DateNavigation
            defaultPeriod={isEmployeeHistory ? 'year' : 'month'}
            defaultDate={selectedDate}
            onDateChange={(date) => setSelectedDate(flattenToDate(date))}
          />
        </DateNavigationCenter>
        <DateNavigationActions>
          {hasRegisterAbsenceAccess && (
            <>
              <CreateButton
                onClick={onClickNew}
                label={t('reactRegisterAbsence.newSicknessNotice')}
                ml={0}
              />
              {/* recording fixed working days belongs to one employee, so
                  this only appears on the employee card; on the quality page
                  the planner is asked after saving a sickness notice */}
              {isEmployeeHistory && (
                <Tooltip
                  title={t('reactRegisterAbsence.workingDaysSettingsTitle')}
                >
                  <IconButton
                    onClick={onClickSettings}
                    sx={{ ml: 1 }}
                    // the gear alone carries no name for screen readers/tests
                    aria-label={t(
                      'reactRegisterAbsence.workingDaysSettingsTitle'
                    )}
                  >
                    <Icon type="gear" fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
        </DateNavigationActions>
      </DateNavigationContainer>

      {!isEmployeeHistory && (
        <EmployeePickerDialog
          open={employeePickerOpen}
          onClose={() => setEmployeePickerOpen(false)}
          onSelect={onEmployeePicked}
        />
      )}

      {settingsEmployee && (
        <WorkingDaysSettingsDialog
          open
          employee={settingsEmployee}
          onClose={() => setSettingsEmployee(null)}
        />
      )}

      {reactDialog && (
        <ReactRegisterAbsenceDialog
          open
          employee={reactDialog.employee}
          notice={reactDialog.notice}
          absenceId={reactDialog.absenceId}
          onClose={() => setReactDialog(null)}
          onSuccess={reactDialog.onSuccess}
        />
      )}

      <TableCRUDLayout
        columns={columns}
        rows={rows}
        editable={false}
        loading={isLoading}
        hideCreateAction
        customActions={(id, row) => {
          // fixed slots (process | edit | employee card): a row without an
          // action keeps an invisible placeholder, so the icons line up in
          // columns instead of shifting per row
          const canProcess =
            hasAbsenceNoticesProcessAccess &&
            row?.status === t('absenceNoticeStatus.RECEIVED');
          // only registered rows carry an absenceId; without the toggle the
          // dojo bridge has no edit flow, so the action stays away entirely
          const canEdit =
            hasRegisterAbsenceAccess &&
            hasFeatureToggleReactRegisterAbsence &&
            !!row?.absenceId &&
            !!row?.employeeDbId;
          const canOpenCard = !isEmployeeHistory && !!row?.employeeDbId;

          return [
            {
              tooltipTitle: t('process'),
              icon: 'check' as IconType,
              placeholder: !canProcess,
              onClick: () => onClickProcess(id as string)
            },
            {
              tooltipTitle: t('reactRegisterAbsence.editTitle'),
              icon: 'pen' as IconType,
              placeholder: !canEdit,
              onClick: () =>
                setReactDialog({
                  // canEdit ensures the row and both ids exist; TS cannot
                  // carry that narrowing into this callback
                  employee: {
                    id: Number(row?.employeeDbId),
                    name: row?.employeeName,
                    number: row?.employeeNumber
                  },
                  absenceId: Number(row?.absenceId),
                  onSuccess: refreshAbsenceNotices
                })
            },
            // the employee card column is absent on the card itself
            ...(isEmployeeHistory
              ? []
              : [
                  {
                    tooltipTitle: t('absenceNoticeOpenEmployeeCard'),
                    icon: 'address-card' as IconType,
                    placeholder: !canOpenCard,
                    onClick: () => {
                      if (row?.employeeDbId) {
                        window.location.href = employeeCardHref(
                          row.employeeDbId
                        );
                      }
                    }
                  }
                ])
          ];
        }}
      />
    </>
  );
};

export default AbsenceNotices;
